import { Connection, OrderLedger } from '@omneo/omneo-sdk'
import Icon from '@/components/snippets/icon/icon'
import { useAccountLayoutContext } from './AccountProfileContext'
import { useCallback, useMemo, useState } from 'preact/hooks'
import { parseFormData } from '../helpers/parseFormData'
import { Separator } from './AccountSectionWrapper'
import { useRefetch } from '../hooks/useRefetch'
import { daysUntilExpiry, getRemainingDaysOnAssignment } from '../helpers/getRemainingDates'
import { AccountRewardsHistory } from './AccountRewardsHistory'
import { Page } from './AccountPage'
import { useProfile } from '../hooks/useProfile'
import { formatMoney } from '@shopify/theme-currency'
import { AccountOrderBadge } from './AccountOrderBadge'
import { accountTranslations } from '../helpers/translations'

const PRODUCT_IMAGE = 'https://cdn.shopify.com/s/files/1/0690/5538/1801/files/4-501874.jpg?v=1720590706&width=960'

const AccountTransactionHeader = () => {
  return <h1 className={'h1'}>{accountTranslations.orders.order_details}</h1>
}

const AccountNameDetails = ({ orderName, status }) => {
  return (
    <div className={'flex flex-row gap-4'}>
      <h4 className={'h4'}>#{orderName}</h4>
      <AccountOrderBadge fulfillmentStatus={status ?? 'pending'} />
    </div>
  )
}

const AccountOrderBack = ({ back }) => {
  const { handlePushState } = useAccountLayoutContext()
  const handleBack = () => {
    handlePushState('orders', true)
  }
  return (
    <div onClick={handleBack} className={'flex flex-row gap-2'}>
      <Icon id="arrow-left" />
      <div className={'btn btn-text btn-inline underline underline-offset-2'}>Back</div>
    </div>
  )
}

/**
 *
 * @param {{back: () => void, order: OrderLedger}} param0
 * @returns
 */
const TransactionHeader = ({ back, transaction }) => {
  const status =
    transaction.isShopifyOrder ||
    !['fulfilled', 'complete'].includes(transaction.fulfillment_status_label ?? transaction.status)
      ? 'pending'
      : 'fulfilled'

  return (
    <div className={'flex scroll-m-16 flex-col gap-6'}>
      <AccountOrderBack back={back} />
      <AccountTransactionHeader />
      <AccountNameDetails orderName={transaction.name || transaction.order_number || transaction.receipt_ref} status={status} />
    </div>
  )
}

const TransactionOrderDetails = ({ transaction }) => {
  const orderDate = new Date(transaction.created_at).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })

  const total = formatMoney(transaction.total * 100)

  const shippingAddress = transaction.shipping_address ?? transaction.shopifyTransaction?.shipping_address

  return (
    <div className={'flex-4 flex'}>
      <div className="flex-1">
        <div className={'paragraph-lg mb-2 font-bold'}>{accountTranslations.orders.order_details}</div>
        <div className={'flex flex-col gap-1'}>
          <div className={'paragraph-md'}>Order Date: {orderDate}</div>
          <div className={'paragraph-md'}>Total: {total}</div>
        </div>
      </div>
      {shippingAddress ? (
        <div className="flex-1">
          <div className={'paragraph-lg mb-2 font-bold'}>Shipping Address</div>
          <div className={'paragraph-md'}>
            {[shippingAddress.address1, shippingAddress.address2]
              .filter((a) => !!a)
              .join(' ')}
          </div>
          <div className={'paragraph-md'}>
            {[shippingAddress.city, shippingAddress.state, shippingAddress.postal_code]
              .filter((a) => !!a)
              .join(' ')}
          </div>
          {shippingAddress.country && <div className={'paragraph-md'}>{shippingAddress.country}</div>}
        </div>
      ) : null}
    </div>
  )
}

const AssignChildBanner = ({ remainingDays }) => {
  const day = remainingDays > 1 ? 'days' : 'day'
  return (
    <div className={'mt-2 w-fit rounded-md bg-secondary-paleblue bg-opacity-[0.24]'}>
      <div className={'flex flex-row items-center gap-2 p-2'}>
        <Icon id="gift" />
        <div>
          <p className={'paragraph-xs leading-4 text-primary-black'}>
            Earn $2 reward for assigning -{' '}
            <b className={'font-bold'}>
              Expires in {remainingDays} {day}
            </b>
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 *
 * @param {{profileChildren : Connection[]}} param0
 * @returns
 */
const AssignChildSelectInput = ({
  profileChildren,
  omneoVariantId,
  omneoClient,
  transactionIdLineId,
  handleRefetch
}) => {
  /**
   * @param {JSXInternal.TargetedSubmitEvent<HTMLFormElement>} e
   */
  const handleAssignLineItemToChildProfile = async (e) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const object = parseFormData(formData, ['id', 'childId', 'lineId'])
    if (object.childId === '-1') return
    const { id, childId, lineId } = object
    await omneoClient.assignItemToChild(lineId, id, childId)
    window.location.reload()
    // handleRefetch()
  }

  return (
    <form onChange={handleAssignLineItemToChildProfile}>
      <input name="id" value={omneoVariantId} type="hidden" />
      <input name="lineId" value={transactionIdLineId} type="hidden" />
      <select className={'min-w-40 rounded-md border-neutrals-medium-grey'} name="childId">
        <option value="-1">Assign child</option>
        {profileChildren.map((profile) => {
          return (
            <option key={profile.id} value={profile.id}>
              {profile.connector.first_name}{' '}
              {profile.connector.last_name !== 'Unknown' ? profile.connector.last_name : ''}
            </option>
          )
        })}
      </select>
    </form>
  )
}

const getProductImage = (item) => {
  const variant = item.product_variant ?? item.variant
  return variant.images?.[0]?.url || item.image || item.product?.featured_image || null
}

/**
 * @param {{item: any, childProfiles: Connection[]}} props
 */
const AccountOrderProductRow = ({
  isModal = false,
  item,
  childProfiles,
  omneoClient,
  handleRefetch,
  unassignedItems,
  transaction = null
}) => {
  const variant = item.product_variant ?? item.variant
  const remainingDays = getRemainingDaysOnAssignment(item?.created_at)
  const productImage = getProductImage(item)
  // This extra check is to ensure we don't display the dropdown to non-shoe items
  const isUnassigned = unassignedItems ? unassignedItems.find((i) => i.id === item.id) : true
  const size = transaction?.isShopifyOrder ? variant.option1 : variant.meta?.shopify?.option1
  const isFreight = ['WEBFREIGHT--', 'WEBFREIGHT__'].includes(item.product_variant.sku)
  const productTitle = isFreight ? 'Shipping' : item?.product_variant?.meta?.shopify?.customer_facing_name ?? item.product.title

  return (
    <div className={`mb-6 flex flex-row gap-4 ${isModal ? 'border-b border-neutrals-medium-grey pb-6' : ''}`}>

        {isFreight ? (
          <div className="flex items-center justify-center w-32 h-32 shrink-0 bg-primary-grey">
            <Icon id="package" className="w-16 h-16" />
          </div>

        ) : (
          <div className={'aspect-1 h-32 w-32'}>
            <img className={'h-full w-full object-cover object-bottom'} src={productImage ?? PRODUCT_IMAGE} />
          </div>
        )}
      <div className={'flex w-full flex-grow flex-col gap-4'}>
        <div className={'flex flex-row justify-between'}>
          <div className={'flex flex-col gap-1'}>
            {!isFreight && <p className={'paragraph-sm font-bold text-primary-black'}>{item.product.brand ?? item.product.vendor}</p>}
            <p className={'paragraph-md text-primary-black'}>{productTitle}{ !isFreight && ` x ${item.quantity}`}</p>
            {!!size && (
              <div className={'flex flex-col'}>
                <p className={'paragraph-sm text-neutrals-secondary-grey'}>{size}</p>
              </div>
            )}
          </div>
          <div>
            {transaction?.isShopifyOrder ? (
              <>
                {item.original_line_price !== item.final_line_price ? (
                  <>
                    <p className={'text-right font-bold'}>{item.final_line_price}</p>
                    <s className={'paragraph-sm text-right text-neutrals-secondary-grey line-through'}>
                      {item.original_line_price}
                    </s>
                  </>
                ) : (
                  <p className={'font-bold'}>{item.final_line_price}</p>
                )}
              </>
            ) : (
              <>
                {item.price_original !== item.price_sell ? (
                  <>
                    <p className={'text-right font-bold'}>{item.price_sell !== 0 ? formatMoney(item.price_sell * 100) : 'FREE'}</p>
                    <s className={'paragraph-sm text-right text-neutrals-secondary-grey line-through'}>
                      {formatMoney(item.price_original * 100)}
                    </s>
                  </>
                ) : (
                  <p className={'font-bold'}>{item.price_sell !== 0 ? formatMoney(item.price_sell * 100) : 'FREE'}</p>
                )}
              </>
            )}
          </div>
        </div>
        <div>
          {remainingDays > 0 && (
            <>
              {!item?.list_items?.length ? (
                isUnassigned ? (
                  <>
                    <AssignChildSelectInput
                      profileChildren={childProfiles}
                      omneoVariantId={item.product_variant_id}
                      omneoClient={omneoClient}
                      transactionIdLineId={item.id}
                      handleRefetch={handleRefetch}
                    />
                    <AssignChildBanner remainingDays={remainingDays} />
                  </>
                ) : null
              ) : (
                <b>Shoe has been Assigned</b>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
/**
 * @param {{props: {order: OrderLedger, childProfiles: Connection[]}}} props
 */
const AccountTransactionProducts = ({ props, omneoClient }) => {
  const { transaction, childProfiles, unassignedItems } = props

  return (
    <div className={'flex flex-col gap-6'}>
      <div className={'paragraph-lg font-bold'}>Products</div>
      {transaction.items.map((item) => {
        return (
          <AccountOrderProductRow
            {...{ item, childProfiles, omneoClient, handleRefetch: props.handleRefetch, unassignedItems, transaction }}
          />
        )
      })}
    </div>
  )
}

/**
 * @param {Order} props
 */
const AccountTransactionDetails = () => {
  return (
    <div className={'flex flex-row justify-between'}>
      <div data-role="order-details" className={'flex w-1/2 flex-col md:my-auto'}>
        <div className={'paragraph-md hidden pb-6 font-bold md:flex md:pb-0'}>{accountTranslations.orders.order_details}</div>
        <div className={'paragraph-sm'}>Order Date: 11</div>
        <div className={'paragraph-sm'}>Total: 1</div>
        <div className={'paragraph-sm'}>Track No: 1</div>
      </div>
      <div data-role="order-details" className={'flex w-1/2 flex-col md:my-auto'}>
        <div className={'paragraph-md hidden pb-6 font-bold md:flex md:pb-0'}>Shipping Address</div>
        <div className={'paragraph-sm'}>Order Date: 11</div>
        <div className={'paragraph-sm'}>Total: 1</div>
        <div className={'paragraph-sm'}>Track No: 1</div>
      </div>
    </div>
  )
}

/**
 *
 * @param {{order: number, shopifyTransactions: any[]}} props
 * @returns
 */
export const AccountTransaction = (props) => {
  const { omneoClient } = useAccountLayoutContext()
  const { refetch, handleRefetch } = useRefetch()
  const [pageData, setPageData] = useState({
    transaction: {},
    unassignedItems: props.unassignedItems,
    childProfiles: [],
    rewards: [],
    handleRefetch
  })

  const transaction = useMemo(
    () => props.transactions.find((o) => o.id === props.selectedOrder),
    [props.transactions, props.selectedOrder]
  )

  const FetchOrderData = useCallback(async () => {
    let children = []
    let rewards = []

    if (!transaction.isShopifyOrder) {
      children = await omneoClient.profileClient.getChildren()
      rewards = await omneoClient.profileClient.getRewards()
    }

    setPageData((c) => ({
      ...c,
      transaction,
      childProfiles: [...children],
      rewards: [...rewards]
    }))
  }, [props.selectedOrder, omneoClient, transaction])

  return (
    <Page
      props={{
        suspense: {
          refetch,
          asyncCallback: FetchOrderData
        }
      }}
    >
      <TransactionHeader transaction={pageData.transaction} />
      <Separator type="0" />
      <TransactionOrderDetails transaction={pageData.transaction} />
      <Separator type="0" />
      <AccountTransactionProducts
        props={pageData}
        omneoClient={omneoClient}
        unassignedItems={pageData.unassignedItems}
      />
      <Separator />
      {!!pageData.rewards?.length && <AccountRewardsHistory props={{ rewards: pageData.rewards }} />}
    </Page>
  )
}
